import React, { useMemo, useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Search, Bell, Menu, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import SearchModal from './SearchModal';
import NotifyMeModal from './NotifyMeModal';

const NavItem = ({ to, label, onClick }) => (
	<NavLink
		to={to}
		onClick={onClick}
		className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'text-white' : 'text-white/80 hover:text-white'}`}
	>
		{label}
	</NavLink>
);

const Navbar = ({ movies = [], moviesLoaded = false, onPlay }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const initialQ = useMemo(() => new URLSearchParams(location.search).get('q') || '', [location.search]);
	const [q] = useState(initialQ);
	const [scrolled, setScrolled] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [notifyModalOpen, setNotifyModalOpen] = useState(false);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 10);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	// eslint-disable-next-line no-unused-vars
	const onSubmit = (e) => {
		e.preventDefault();
		if (q.trim()) {
			navigate(`/search?q=${encodeURIComponent(q.trim())}`);
		}
	};

	const headerBg = scrolled ? 'bg-black/40 backdrop-blur-md border-b border-white/10' : 'bg-transparent border-transparent';

	return (
		<header className={`fixed top-0 w-full z-50 transition-colors duration-300 ${headerBg}`}>
			<div className="container mx-auto px-4 py-4 flex items-center justify-between">
				{/* Left: Logo + burger */}
				<div className="flex items-center gap-3">
					<Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
						<Dialog.Trigger asChild>
							<button className="md:hidden p-2 rounded-full bg-white/10 border border-white/10 text-white">
								<Menu size={18} />
							</button>
						</Dialog.Trigger>
						<Dialog.Portal>
							<Dialog.Overlay className="fixed inset-0 bg-black/70 z-[60]" />
							<Dialog.Content className="fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-[#141414] border-l border-white/10 p-4 z-[70]">
								<div className="flex items-center justify-between mb-4">
									<span className="text-white/90 font-medium">Menu</span>
									<Dialog.Close className="p-2 rounded-full bg-white/10 border border-white/10 text-white">
										<X size={18} />
									</Dialog.Close>
								</div>
								<nav className="flex flex-col gap-2">
									<NavItem to="/" label="Movies & Shows" onClick={() => setMobileOpen(false)} />
									<NavItem to="/blog" label="Blog" onClick={() => setMobileOpen(false)} />
									<NavItem to="/about" label="About" onClick={() => setMobileOpen(false)} />
									<NavItem to="/support" label="Support" onClick={() => setMobileOpen(false)} />
								</nav>
							</Dialog.Content>
						</Dialog.Portal>
					</Dialog.Root>
					<Link to="/" className="flex items-center">
						<img 
							src="/Noilio Logo-white.svg" 
							alt="Noilio" 
							className="h-10 w-auto"
						/>
					</Link>
				</div>

				{/* Center: Glass pill nav (hidden on mobile) */}
				<nav className="hidden md:block">
					<div className="rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-2 py-1 flex items-center gap-1">
						<NavItem to="/" label="Movies & Shows" />
						<NavItem to="/blog" label="Blog" />
						<NavItem to="/about" label="About" />
						<NavItem to="/support" label="Support" />
					</div>
				</nav>

				{/* Right: Search + bell */}
				<div className="flex items-center gap-2">
					{/* Desktop Search Button */}
					<button 
						onClick={() => setSearchOpen(true)}
						className="hidden md:flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-3 py-1.5 hover:bg-white/20 transition-colors"
					>
						<Search size={16} className="text-white/70" />
						<span className="text-sm text-white/60">Search</span>
					</button>
					
					{/* Mobile Search Button */}
					<button 
						onClick={() => setSearchOpen(true)}
						className="md:hidden p-2 rounded-full bg-white/10 border border-white/10 text-white hover:bg-white/20 transition-colors"
					>
						<Search size={18} />
					</button>
					
					<button 
						onClick={() => setNotifyModalOpen(true)}
						className="p-2 rounded-full bg-white/10 border border-white/10 text-white hover:bg-white/20 transition-colors"
					>
						<Bell size={18} />
					</button>
				</div>
			</div>
			
			{/* Search Modal */}
			{moviesLoaded && (
				<SearchModal 
					isOpen={searchOpen} 
					onClose={() => setSearchOpen(false)} 
					movies={movies} 
					onPlay={onPlay} 
				/>
			)}
			
			{/* Notify Me Modal */}
			<NotifyMeModal 
				isOpen={notifyModalOpen} 
				onClose={() => setNotifyModalOpen(false)} 
			/>
		</header>
	);
};

export default Navbar; 